// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "redux-rtk/rootReducer";
import { toast } from "react-toastify";
import queryString from 'query-string';
import { flatten } from 'q-flat'

import type { BaseQueryFn } from '@reduxjs/toolkit/query'
import axios from 'axios'
import type { AxiosRequestConfig, AxiosError } from 'axios'

enum ActionTypes {
  REHYDRATE = "REHYDRATE",
}
interface Headers {
  [key: string]: string;
}
interface IAxiosBaseQuery {
  baseUrl?: string;
  headers?: Headers;
}

const axiosBaseQuery =
  (
    { baseUrl, headers }: IAxiosBaseQuery
  ): BaseQueryFn<
    {
      url: string
      method: AxiosRequestConfig['method']
      data?: AxiosRequestConfig['data']
      params?: AxiosRequestConfig['params']
    },
    unknown,
    unknown
  > =>
    async ({ url, method, data, params }, { signal, getState }) => {
      try {
        let uState = (getState() as RootState)?.Auth.user;
        let mainHeaders = {
          'Accept' : 'application/json',
          'Authorization': `Bearer ${uState.token}`
        };

        const result = await axios({ url: baseUrl + url, method, data, params, 
          headers: {
            ...headers, ...mainHeaders
          },
        })
        return { data: result.data }
      } catch (axiosError) {
        console.log(axiosError)
        if (typeof axiosError === 'string') {
          toast.error(axiosError, { autoClose: 10000 });
          switch (axiosError) {
            case 'Invalid credentials':
              window.location.href = "/auth/login";
              break;
          
            default:
              break;
          }
        }
        let err = axiosError as AxiosError
        return {
          error: {
            status: err?.response?.status,
            data: err?.response?.data || err?.message,
          },
        }
      }
    }

// Define a service using a base URL and expected endpoints
export const api = createApi({
  reducerPath: "api",
  tagTypes: [
    "OnDemandDashboard",
    "OnDemandEvent",
    "OnDemandPayment",
    "OnDemandEventTestimonial",
    "OnDemandInvoice",
    "OnDemandRundown",
    "OnDemandMDataLayanan",
    "OnDemandMData",
    "OnDemandMDetail",
    "OnDemandExport",
    "OnDemandVoucherProvider",
    "OnDemandVoucherCode",

    "KambojaCustomer",
    "KambojaWishlist",

    // Form Builder
    "Form",
    "Group",
    "Field",
    "Response",
    "ResponseField",
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL || '',
  }),
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === ActionTypes.REHYDRATE) {
      return action.payload[reducerPath];
    }
  },
  endpoints: (builder) => ({
    getOndemandDashboard: builder.query({
      // query: (params) =>
      //   "ondemand/event" +
      //   (params ? "?"+new URLSearchParams(params).toString() : ""),
      query: (params) => ({
        // url: "/ondemand/dashboard" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        url: "/ondemand/dashboard",
        params: params,
        method: "GET",
      }),
      providesTags: ["OnDemandDashboard"],
      // keepUnusedDataFor: 1,
    }),
    exportOndemand: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/dashboard/export",
        method: "POST",
        data: payload,
        responType: "blob"
      }),
      invalidatesTags: ["OnDemandExport"],
    }),

    // OnDemand - Event
    getOndemandEvent: builder.query({
      // query: (params) =>
      //   "ondemand/event" +
      //   (params ? "?"+new URLSearchParams(params).toString() : ""),
      query: (params) => ({
        url: "/ondemand/event" + (params ? "?"+queryString.stringify(flatten(params), {encode:false}) : ""),
        method: "GET",
      }),
      providesTags: ["OnDemandEvent"],
      // keepUnusedDataFor: 1,
    }),
    addOndemandEvent: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/event/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandEvent"],
    }),
    updateOndemandEvent: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/event/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandEvent"],
    }),
    deleteOndemandEvent: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/event/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandEvent"],
    }),
    exportOndemandEvent: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/event/export",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandEvent"],
    }),
    exportAdminOndemandEvent: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/event/export-admin",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandEvent"],
    }),

    getOndemandEventTestimonial: builder.query({
      // query: (params) =>
      //   "ondemand/event" +
      //   (params ? "?"+new URLSearchParams(params).toString() : ""),
      query: (params) => ({
        url: "/ondemand/event/testimonial" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["OnDemandEvent"],
      // keepUnusedDataFor: 1,
    }),
    uploadOndemandEvent: builder.mutation<{}, FormData>({
      query: (payload) => ({
        url: "/ondemand/event/update",
        method: "POST",
        credentials: "include",
        data: payload,
      }),
      invalidatesTags: ["OnDemandEvent"],
    }),
    deleteOndemandEventTestimonial: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/event/testimonial/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandEvent"],
    }),
    addOndemandEventPayment: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/event/addPayment",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandEvent"],
    }),
    generatePdfEvent: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/event/generatePdf",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandEvent"],
    }),
    filterInvoiceDetailEvent: builder.query({
      // query: (params) =>
      //   "ondemand/event" +
      //   (params ? "?"+new URLSearchParams(params).toString() : ""),
      query: (params) => ({
        url: "/ondemand/event/filterInvoiceDetail" + (params ? "?"+queryString.stringify(flatten(params), {encode:false}) : ""),
        method: "GET",
      }),
      providesTags: ["OnDemandEvent"],
      // keepUnusedDataFor: 1,
    }),
    filterNamaTPUEvent: builder.query({
      // query: (params) =>
      //   "ondemand/event" +
      //   (params ? "?"+new URLSearchParams(params).toString() : ""),
      query: (params) => ({
        url: "/ondemand/event/filterNamaTPU" + (params ? "?"+queryString.stringify(flatten(params), {encode:false}) : ""),
        method: "GET",
      }),
      providesTags: ["OnDemandEvent"],
      // keepUnusedDataFor: 1,
    }),
    filterFormTemplateEvent: builder.query({
      // query: (params) =>
      //   "ondemand/event" +
      //   (params ? "?"+new URLSearchParams(params).toString() : ""),
      query: (params) => ({
        url: "/ondemand/event/filterFormTemplate" + (params ? "?"+queryString.stringify(flatten(params), {encode:false}) : ""),
        method: "GET",
      }),
      providesTags: ["OnDemandEvent"],
      // keepUnusedDataFor: 1,
    }),

    // OnDemand - Event - Payment
    getOndemandPayment: builder.query({
      query: (params) => ({
        url: "/ondemand/payment" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["OnDemandPayment"],
      // keepUnusedDataFor: 1,
    }),
    addOndemandPayment: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/payment/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandPayment"],
    }),
    updateOndemandPayment: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/payment/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandPayment"],
    }),
    deleteOndemandPayment: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/payment/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandPayment"],
    }),

    // OnDemand - Event - Invoice
    getOndemandInvoice: builder.query({
      query: (params) => ({
        url: "/ondemand/invoice" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["OnDemandInvoice"],
      // keepUnusedDataFor: 1,
    }),
    addOndemandInvoice: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/invoice/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandInvoice"],
    }),
    updateOndemandInvoice: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/invoice/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandInvoice"],
    }),
    deleteOndemandInvoice: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/invoice/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandInvoice"],
    }),
    resendOndemandInvoice: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/invoice/resendEmail",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandInvoice"],
    }),
    requestFinalPaymentOndemandInvoice: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/invoice/requestFinalPayment",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandInvoice"],
    }),

    // OnDemand - Event - Rundown
    getOndemandRundown: builder.query({
      query: (params) => ({
        url: "/ondemand/rundown" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["OnDemandRundown"],
      // keepUnusedDataFor: 1,
    }),
    addOndemandRundown: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/rundown/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandRundown"],
    }),
    updateOndemandRundown: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/rundown/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandRundown"],
    }),
    deleteOndemandRundown: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/rundown/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandRundown"],
    }),
    sendEmailOndemandRundown: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/rundown/sendEmail",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandRundown"],
    }),
    importOndemandRundown: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/rundown/import",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandRundown"],
    }),

    // OnDemand - Event - MDataLayanan
    getOndemandMDataLayanan: builder.query({
      query: (params) => ({
        url: "/ondemand/mdata-layanan" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["OnDemandMDataLayanan"],
      // keepUnusedDataFor: 1,
    }),
    searchOndemandMDataLayanan: builder.mutation({
      query: (params) => ({
        url: "/ondemand/mdata-layanan" + (params ? "?"+queryString.stringify(flatten(params), {encode:false}) : ""),
        method: "GET",
      }),
      invalidatesTags: ["OnDemandMDataLayanan"],
    }),
    addOndemandMDataLayanan: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/mdata-layanan/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandMDataLayanan"],
    }),
    updateOndemandMDataLayanan: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/mdata-layanan/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandMDataLayanan"],
    }),
    deleteOndemandMDataLayanan: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/mdata-layanan/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandMDataLayanan"],
    }),

    // OnDemand - Master Data &
    getOndemandMData: builder.query({
      query: (params) => ({
        url: "/ondemand/mdata" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["OnDemandMData"],
      // keepUnusedDataFor: 1,
    }),
    addOndemandMData: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/mdata/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandMData"],
    }),
    duplicateOndemandMData: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/mdata/duplicate",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandMData"],
    }),
    updateOndemandMData: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/mdata/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandMData"],
    }),
    deleteOndemandMData: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/mdata/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandMData"],
    }),
    getOndemandMDetail: builder.query({
      query: (params) => ({
        url: "/ondemand/mdetail" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["OnDemandMDetail"],
      // keepUnusedDataFor: 1,
    }),
    addOndemandMDetail: builder.mutation<{}, FormData>({
      query: (payload) => ({
        url: "/ondemand/mdetail/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandMDetail"],
    }),
    updateOndemandMDetail: builder.mutation<{}, FormData>({
      query: (payload) => ({
        url: "/ondemand/mdetail/update",
        method: "POST",
        credentials: "include",
        data: payload,
      }),
      invalidatesTags: ["OnDemandMDetail"],
    }),
    deleteOndemandMDetail: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/mdetail/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandMDetail"],
    }),

    // Form Builder - Form
    getForm: builder.query({
      query: (params) => ({
        url: "/form-builder/form" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["Form"],
      // keepUnusedDataFor: 1,
    }),
    addForm: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/form/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Form"],
    }),
    duplicateForm: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/form/duplicate",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Form"],
    }),
    updateForm: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/form/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Form"],
    }),
    deleteForm: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/form/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Form"],
    }),
    updateFormEventRelation: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/form/updateEventRelation",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Form"],
    }),

    // Form Builder - Group
    getGroup: builder.query({
      query: (params) => ({
        url: "/form-builder/group" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["Group"],
      // keepUnusedDataFor: 1,
    }),
    addGroup: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/group/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Group"],
    }),
    updateGroup: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/group/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Group"],
    }),
    deleteGroup: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/group/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Group"],
    }),

    // Form Builder - Field
    getField: builder.query({
      query: (params) => ({
        url: "/form-builder/field" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["Field"],
      // keepUnusedDataFor: 1,
    }),
    addField: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/field/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Field"],
    }),
    updateField: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/field/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Field"],
    }),
    updateSilentField: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/field/update",
        method: "POST",
        data: payload,
      }),
    }),
    deleteField: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/field/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Field"],
    }),

    // Form Builder - Response
    getResponse: builder.query({
      query: (params) => ({
        url: "/form-builder/response" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["Response"],
      // keepUnusedDataFor: 1,
    }),
    getGroupedResponse: builder.query({
      query: (params) => ({
        url: "/form-builder/response/grouping" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["Response"],
      // keepUnusedDataFor: 1,
    }),
    addResponse: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/response/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Response"],
    }),
    updateResponse: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/response/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Response"],
    }),
    deleteResponse: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/response/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Response"],
    }),
    approveResponse: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/response/approve",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Response"],
    }),
    exportResponse: builder.mutation({
      query: (payload) => ({
        url: "/form-builder/response/export",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Response"],
    }),

    // Form Builder - Response - Field
    getResponseField: builder.query({
      query: (params) => ({
        url: "/form-builder/response/field" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["ResponseField"],
      // keepUnusedDataFor: 1,
    }),

    // OnDemand - Voucher Provider
    getVoucherProvider: builder.query({
      query: (params) => ({
        url: "/ondemand/voucher-provider" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["OnDemandVoucherProvider"],
      // keepUnusedDataFor: 1,
    }),
    addVoucherProvider: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/voucher-provider/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandVoucherProvider"],
    }),
    updateVoucherProvider: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/voucher-provider/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandVoucherProvider"],
    }),
    deleteVoucherProvider: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/voucher-provider/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandVoucherProvider"],
    }),

    // OnDemand - Voucher Code
    getVoucherCode: builder.query({
      query: (params) => ({
        url: "/ondemand/voucher-code" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["OnDemandVoucherCode"],
      // keepUnusedDataFor: 1,
    }),
    addVoucherCode: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/voucher-code/create",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandVoucherCode"],
    }),
    updateVoucherCode: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/voucher-code/update",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandVoucherCode"],
    }),
    deleteVoucherCode: builder.mutation({
      query: (payload) => ({
        url: "/ondemand/voucher-code/delete",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["OnDemandVoucherCode"],
    }),

    // Kamboja Customer
    getKambojaCustomer: builder.query({
      // query: (params) =>
      //   "kamboja/customer" +
      //   (params ? "?"+new URLSearchParams(params).toString() : ""),
      // providesTags: ["KambojaCustomer"],
      query: (params) => ({
        url: "/kamboja/customer" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      // keepUnusedDataFor: 60 * 60,
    }),

    // Kamboja Wishlist
    getKambojaWishlist: builder.query({
      // query: (params) =>
      //   "kamboja/wishlist" +
      //   (params ? "?" + "?"+new URLSearchParams(params).toString() : ""),
      query: (params) => ({
        url: "/kamboja/wishlist" + (params ? "?"+new URLSearchParams(params).toString() : ""),
        method: "GET",
      }),
      providesTags: ["KambojaWishlist"],
      // keepUnusedDataFor: 60 * 60,
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  // On Demand Dashboard
  useGetOndemandDashboardQuery,
  
  // On Demand Export
  useExportOndemandMutation,

  // On Demand Event
  useGetOndemandEventQuery,
  useAddOndemandEventMutation,
  useUpdateOndemandEventMutation,
  useDeleteOndemandEventMutation,
  useExportOndemandEventMutation,
  useExportAdminOndemandEventMutation,
  useAddOndemandEventPaymentMutation,
  useGeneratePdfEventMutation,
  useFilterInvoiceDetailEventQuery,
  useFilterNamaTPUEventQuery,
  useFilterFormTemplateEventQuery,

  // On Demand Event Payment
  useGetOndemandPaymentQuery,
  useAddOndemandPaymentMutation,
  useUpdateOndemandPaymentMutation,
  useDeleteOndemandPaymentMutation,

  // On Demand Invoice
  useGetOndemandInvoiceQuery,
  useAddOndemandInvoiceMutation,
  useUpdateOndemandInvoiceMutation,
  useDeleteOndemandInvoiceMutation,
  useResendOndemandInvoiceMutation,
  useRequestFinalPaymentOndemandInvoiceMutation,

  // On Demand Rundown
  useGetOndemandRundownQuery,
  useAddOndemandRundownMutation,
  useUpdateOndemandRundownMutation,
  useDeleteOndemandRundownMutation,
  useSendEmailOndemandRundownMutation,
  useImportOndemandRundownMutation,

  // On Demand MDataLayanan
  useGetOndemandMDataLayananQuery,
  useSearchOndemandMDataLayananMutation,
  useAddOndemandMDataLayananMutation,
  useUpdateOndemandMDataLayananMutation,
  useDeleteOndemandMDataLayananMutation,

  // On Demand Event - Testimonial
  useGetOndemandEventTestimonialQuery,
  useUploadOndemandEventMutation,
  useDeleteOndemandEventTestimonialMutation,

  // On Demand Master Data
  useGetOndemandMDataQuery,
  useAddOndemandMDataMutation,
  useDuplicateOndemandMDataMutation,
  useUpdateOndemandMDataMutation,
  useDeleteOndemandMDataMutation,
  useGetOndemandMDetailQuery,
  useAddOndemandMDetailMutation,
  useUpdateOndemandMDetailMutation,
  useDeleteOndemandMDetailMutation,

  // Form Builder - Form
  useGetFormQuery,
  useAddFormMutation,
  useDuplicateFormMutation,
  useUpdateFormMutation,
  useDeleteFormMutation,
  useUpdateFormEventRelationMutation,

  // Form Builder - Group
  useGetGroupQuery,
  useAddGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,

  // Form Builder - Field
  useGetFieldQuery,
  useAddFieldMutation,
  useUpdateFieldMutation,
  useUpdateSilentFieldMutation,
  useDeleteFieldMutation,

  // Response Builder - Response
  useGetResponseQuery,
  useGetGroupedResponseQuery,
  useAddResponseMutation,
  useUpdateResponseMutation,
  useDeleteResponseMutation,
  useApproveResponseMutation,
  useExportResponseMutation,

  // Voucher Provider
  useGetVoucherProviderQuery,
  useAddVoucherProviderMutation,
  useUpdateVoucherProviderMutation,
  useDeleteVoucherProviderMutation,

  // Voucher Code
  useGetVoucherCodeQuery,
  useAddVoucherCodeMutation,
  useUpdateVoucherCodeMutation,
  useDeleteVoucherCodeMutation,

  // Response Builder - Response - Field
  useGetResponseFieldQuery,

  // Kamboja
  useGetKambojaCustomerQuery,
  useGetKambojaWishlistQuery,
} = api;
